import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import { fetchHelpCenterData, fetchHelpCategories, fetchHelpArticleBySlug, submitHelpTicket } from '../api/GlobalApi';
import { useParams, useNavigate } from 'react-router-dom';

function HelpCenterPage() {
  const [helpData, setHelpData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();


  useEffect(() => {
      window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadHelpCenter();
  }, []);

  const loadHelpCenter = async () => {
    try {
      setLoading(true);
      const [data, cats] = await Promise.all([
        fetchHelpCenterData(),
        fetchHelpCategories()
      ]);
      setHelpData(data);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Error loading help center:', error);
      setHelpData({ popularArticles: [], quickLinks: [] });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/help/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  return (
    <main className="main-content">
      {/* Hero Section */}
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>How can we help you?</h1>
        <p style={styles.heroSubtitle}>Search for answers or browse categories below</p>
        
        <form onSubmit={handleSearch} style={styles.searchForm}>
          <div style={styles.searchBox}>
            <svg style={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.searchInput}
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} style={styles.clearBtn}>
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                </svg>
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Categories Grid */}
      <div style={styles.container}>
        <h2 style={styles.sectionTitle}>Browse by Category</h2>
        <div style={styles.categoriesGrid}>
          {categories.map((category) => (
            <a
              key={category.id}
              href={`/help/category/${category.slug}`}
              style={styles.categoryCard}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <div style={styles.categoryIcon}>
                <svg width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
                  <path d={getCategoryIcon(category.slug)} />
                </svg>
              </div>
              <h3 style={styles.categoryTitle}>{category.name}</h3>
              <p style={styles.categoryDesc}>{category.description}</p>
              <span style={styles.articleCount}>{category.article_count} articles</span>
            </a>
          ))}
        </div>
      </div>

      {/* Popular Articles */}
      {helpData?.popularArticles?.length > 0 && (
        <div style={styles.container}>
          <h2 style={styles.sectionTitle}>Popular Articles</h2>
          <div style={styles.articlesList}>
            {helpData.popularArticles.map((article) => (
              <a
                key={article.id}
                href={`/help/article/${article.slug}`}
                style={styles.articleItem}
              >
                <div style={styles.articleLeft}>
                  <svg style={styles.articleIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <h4 style={styles.articleTitle}>{article.title}</h4>
                    <p style={styles.articleExcerpt}>{article.excerpt}</p>
                  </div>
                </div>
                <svg style={styles.chevronRight} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div style={styles.container}>
        <h2 style={styles.sectionTitle}>Quick Links</h2>
        <div style={styles.quickLinksGrid}>
          <a href="/faqs" style={styles.quickLink}>
            <svg style={styles.quickLinkIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>FAQs</span>
          </a>
          <a href="/contact" style={styles.quickLink}>
            <svg style={styles.quickLinkIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Contact Support</span>
          </a>
          <a href="/privacy-policy" style={styles.quickLink}>
            <svg style={styles.quickLinkIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Privacy Policy</span>
          </a>
          <a href="/terms" style={styles.quickLink}>
            <svg style={styles.quickLinkIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Terms of Service</span>
          </a>
        </div>
      </div>

      {/* Contact CTA */}
      <div style={styles.ctaSection}>
        <div style={styles.ctaCard}>
          <svg style={styles.ctaIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <h3 style={styles.ctaTitle}>Can't find what you're looking for?</h3>
          <p style={styles.ctaText}>
            Our support team is here to help you with any questions or issues.
          </p>
          <Link to="/contact" style={styles.ctaBtn}>
            Get in Touch
          </Link>
        </div>
      </div>
    </main>
  );
}

// Helper function for category icons
const getCategoryIcon = (slug) => {
  const icons = {
    'getting-started': 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    'account': 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    'billing': 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    'technical': 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    default: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z'
  };
  return icons[slug] || icons.default;
};

const styles = {
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '6px solid #ddd',
    borderTop: '6px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  hero: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '80px 20px',
    textAlign: 'center',
    color: 'white',
    borderRadius: '20px',
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '600',
    marginBottom: '16px',
    margin: 0,
  },
  heroSubtitle: {
    fontSize: '20px',
    opacity: 0.9,
    marginBottom: '40px',
  },
  searchForm: {
    maxWidth: '720px',
    margin: '0 auto',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '24px',
    width: '24px',
    height: '24px',
    color: '#5f6368',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    height: '64px',
    padding: '0 60px',
    fontSize: '18px',
    border: 'none',
    borderRadius: '32px',
    outline: 'none',
    backgroundColor: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  clearBtn: {
    position: 'absolute',
    right: '24px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#5f6368',
    borderRadius: '50%',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '60px 20px',
  },
  sectionTitle: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '32px',
    textAlign: 'center',
  },
  categoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
  },
  categoryCard: {
    padding: '32px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '16px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  categoryIcon: {
    width: '64px',
    height: '64px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    marginBottom: '20px',
  },
  categoryTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '8px',
  },
  categoryDesc: {
    fontSize: '14px',
    color: '#5f6368',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  articleCount: {
    fontSize: '13px',
    color: '#1a73e8',
    fontWeight: '500',
  },
  articlesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  articleItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  articleLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    flex: 1,
  },
  articleIcon: {
    width: '24px',
    height: '24px',
    color: '#5f6368',
    flexShrink: 0,
    marginTop: '2px',
  },
  articleTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '4px',
  },
  articleExcerpt: {
    fontSize: '14px',
    color: '#5f6368',
    lineHeight: '1.5',
  },
  chevronRight: {
    width: '20px',
    height: '20px',
    color: '#5f6368',
    flexShrink: 0,
  },
  quickLinksGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  quickLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '20px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    textDecoration: 'none',
    color: '#202124',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  quickLinkIcon: {
    width: '24px',
    height: '24px',
    color: '#667eea',
  },
  ctaSection: {
    maxWidth: '1200px',
    margin: '0 auto 60px',
    padding: '0 20px',
  },
  ctaCard: {
    textAlign: 'center',
    padding: '60px 32px',
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    borderRadius: '20px',
    color: 'white',
  },
  ctaIcon: {
    width: '56px',
    height: '56px',
    margin: '0 auto 24px',
  },
  ctaTitle: {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  ctaText: {
    fontSize: '18px',
    opacity: 0.95,
    marginBottom: '32px',
    maxWidth: '600px',
    margin: '0 auto 32px',
  },
  ctaBtn: {
    display: 'inline-block',
    padding: '16px 40px',
    background: 'white',
    color: '#f5576c',
    borderRadius: '28px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '16px',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
};

const styleSheet = document.createElement('style');
styleSheet.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (hover: hover) {
    .categoryCard:hover {
      box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    }
    .articleItem:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      border-color: #1a73e8;
    }
    .quickLink:hover {
      background: #f8f9fa;
      border-color: #667eea;
    }
    .ctaBtn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0,0,0,0.2);
    }
  }

  @media (max-width: 768px) {
    .heroTitle {
      font-size: 36px !important;
    }
    .sectionTitle {
      font-size: 24px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HelpCenterPage;